import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useContext, useEffect } from 'react';
import { Stack } from 'syf-component-library/ui/spacing';
import { Small } from 'syf-component-library/ui/typography';
import { MESSAGES, PAGE_FUNCTION, PAGE_NAME, PaymentMethodToPageSubFunctionText } from '../../../../const/analytics';
import { CHECKING, CREDIT_CARD, PINLESS_DEBIT_CARD, SAVINGS } from '../../../../const/paymentMedium';
import MainContext from '../../../../Context';
import getDCHMessageType from '../../../../helpers/getDCHMessageType';
import useApiMachine from '../../../../hooks/machines/useApiMachine';
import usePaymentMethodMachine from '../../../../hooks/machines/usePaymentMethodMachine';
import useAnalyticsEventV2Creators from '../../../../hooks/useAnalyticsEventV2Creators';
import { t } from '../../../../strings/i18n';
import { AnalyticsEventFeatures } from '../../../../typings/analytics';
import MULTI_PAYMENT_FLOW_TABS from '../../../../const/multiPaymentFlowTabs';
import PaymentMethodTypeSelector from '../PaymentMethodTypeSelector';
import CheckingSavingsAccountForm from './CheckingSavingsAccountForm';
import DebitCreditAccountForm from './DebitCreditAccountForm';
const PaymentMethodForm = ({ className, errorMessage, setFormErrorMessage, setIsSubmitting }) => {
    const { featureFlags, triggerAnalyticsEvent, multiPaymentFlowCurrentTab } = useContext(MainContext);
    const { createStartEventObject, createConfirmationEventObject } = useAnalyticsEventV2Creators(AnalyticsEventFeatures.ADD_PAYMENT_METHOD);
    const [apiMachine] = useApiMachine();
    const { data: accountSummary } = apiMachine.context.accountSummaryQueryResult;
    const productType = accountSummary.product.type;
    const [paymentMethodMachine, sendToPaymentMethod] = usePaymentMethodMachine();
    // TODO: Remove this when we deprecate the isManagePaymentMethodsEnabled featureFlag
    const legacyIsChecking = paymentMethodMachine.matches('addNewCheckingAccount');
    const legacyIsSavings = paymentMethodMachine.matches('addNewSavingsAccount');
    const legacyIsDebit = paymentMethodMachine.matches('addNewDebitCard');
    const legacyIsCredit = paymentMethodMachine.matches('addNewCreditCard');
    // End Todo
    const isChecking = legacyIsChecking ||
        paymentMethodMachine.matches('addPaymentMethod.addNewCheckingAccount');
    const isSavings = legacyIsSavings ||
        paymentMethodMachine.matches('addPaymentMethod.addNewSavingsAccount');
    const isDebit = legacyIsDebit ||
        paymentMethodMachine.matches('addPaymentMethod.addNewDebitCard');
    const isCredit = legacyIsCredit ||
        paymentMethodMachine.matches('addPaymentMethod.addNewCreditCard');
    const isBankAccount = isChecking || isSavings;
    const isCardAccount = isDebit || isCredit;
    const isMultiPaymentTab = multiPaymentFlowCurrentTab === MULTI_PAYMENT_FLOW_TABS.MULTI;
    const [paymentMethodType] = [
        [SAVINGS, isSavings],
        [CHECKING, isChecking],
        [PINLESS_DEBIT_CARD, isDebit],
        [CREDIT_CARD, isCredit]
    ].find(([, matches]) => {
        return matches;
    });
    const messageTypeDCH = getDCHMessageType(featureFlags.isDigitalCollectionsEnabled, accountSummary, multiPaymentFlowCurrentTab);
    useEffect(() => {
        const eventOptionsV2 = createStartEventObject();
        const pageSubFunctionText = PaymentMethodToPageSubFunctionText[paymentMethodType];
        const eventOptionsV1 = Object.assign(Object.assign(Object.assign({ pageFunction: PAGE_FUNCTION.MAKE_PAYMENT.PAYMENTS, pageSubFunction: pageSubFunctionText }, (isMultiPaymentTab && { message: MESSAGES.MULTI_PAYMENT })), (messageTypeDCH && { messageType: messageTypeDCH })), { pageName: PAGE_NAME.START });
        triggerAnalyticsEvent(eventOptionsV1, eventOptionsV2);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    const handlePaymentMethodTypeChange = (value) => {
        switch (value) {
            case CHECKING:
                sendToPaymentMethod({ type: 'GO_TO_CHECKING' });
                break;
            case SAVINGS:
                sendToPaymentMethod({ type: 'GO_TO_SAVINGS' });
                break;
            case PINLESS_DEBIT_CARD:
                sendToPaymentMethod({ type: 'GO_TO_DEBIT' });
                break;
            case CREDIT_CARD:
                sendToPaymentMethod({ type: 'GO_TO_CREDIT' });
                break;
            default:
                break;
        }
        setFormErrorMessage('');
        const pageSubFunctionText = PaymentMethodToPageSubFunctionText[value];
        const eventOptionsV1 = Object.assign(Object.assign(Object.assign({ pageFunction: PAGE_FUNCTION.MAKE_PAYMENT.PAYMENTS, pageSubFunction: pageSubFunctionText }, (messageTypeDCH && { messageType: messageTypeDCH })), (isMultiPaymentTab && { message: MESSAGES.MULTI_PAYMENT })), { pageName: PAGE_NAME.START });
        triggerAnalyticsEvent(eventOptionsV1);
    };
    const handleComplete = (paymentFrom) => {
        const eventOptionsV1 = Object.assign(Object.assign({ pageFunction: PAGE_FUNCTION.MAKE_PAYMENT.PAYMENTS, pageSubFunction: PaymentMethodToPageSubFunctionText[paymentMethodType], pageName: PAGE_NAME.COMPLETE }, (isMultiPaymentTab && { message: MESSAGES.MULTI_PAYMENT })), (messageTypeDCH && { messageType: messageTypeDCH }));
        const eventOptionsV2 = createConfirmationEventObject(paymentFrom, paymentMethodType);
        triggerAnalyticsEvent(eventOptionsV1, eventOptionsV2);
    };
    return (_jsxs(_Fragment, { children: [_jsx(PaymentMethodTypeSelector, { errorMessage: errorMessage, featureFlags: featureFlags, handlePaymentMethodTypeChange: handlePaymentMethodTypeChange, paymentMethodType: paymentMethodType, productType: productType }), _jsx(Stack, { size: "xlarge" }), isBankAccount ? (_jsx(CheckingSavingsAccountForm, { className: className, setFormErrorMessage: setFormErrorMessage, setIsSubmitting: setIsSubmitting, onComplete: handleComplete })) : null, isCardAccount ? (_jsxs(_Fragment, { children: [_jsx(DebitCreditAccountForm, { className: className, setFormErrorMessage: setFormErrorMessage, setIsSubmitting: setIsSubmitting, onComplete: handleComplete }), _jsx(Stack, { size: "xxlarge" })] })) : null, _jsx(Small, Object.assign({ "data-cy": "payment-method-primary-disclosure-text", id: "primaryDisclosureText" }, { children: t('addPaymentMethod.primaryDisclosureText') })), _jsx("br", {}), _jsx(Small, Object.assign({ "data-cy": "payment-method-secondary-disclosure-text", id: "secondaryDisclosureText" }, { children: t('addPaymentMethod.secondaryDisclosureText') }))] }));
};
export default PaymentMethodForm;
